import { CalmIllustration } from "@/components/illustrations";
import { recordError } from "@/services/record-error";

import { useEffect, useState } from "react";
import { Link, isRouteErrorResponse, useRouteError } from "react-router";

// After a deployment, an open tab still points to the previous build's files.
// Each browser words the failed dynamic import differently.
function isStaleChunkError(error) {
  const message = String(error?.message || error || "");
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("error loading dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("Unable to preload CSS")
  );
}

// When the visitor leaves the page (link to another document, redirect,
// address bar), WebKit aborts the lazy route being loaded with the same
// message as a stale build: "Importing a module script failed". Reloading then
// would cancel the navigation and keep the visitor on the page they were
// leaving, so the automatic reload is skipped once the page starts unloading.
// The abort happens before "pagehide", hence "beforeunload". A "beforeunload"
// cancelled by an unsaved-changes prompt keeps the visitor here, and
// "pageshow" brings the page back from the back-forward cache: both reset it.
let pageUnloading = false;
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (event) => {
    pageUnloading = true;
    setTimeout(() => {
      if (event.defaultPrevented) pageUnloading = false;
    }, 0);
  });
  window.addEventListener("pagehide", () => {
    pageUnloading = true;
  });
  window.addEventListener("pageshow", () => {
    pageUnloading = false;
  });
}

// Claims the automatic reload and records when it happened. One per minute
// at most, so a real outage never loops.
function claimStaleChunkReload() {
  try {
    const lastReloadAt = Number(
      window.sessionStorage.getItem("volvamed.stale-chunk-reload-at") || 0
    );
    if (Date.now() - lastReloadAt < 60 * 1000) return false;
    window.sessionStorage.setItem(
      "volvamed.stale-chunk-reload-at",
      String(Date.now())
    );
    return true;
  } catch {
    return false;
  }
}

function getContent(error, staleChunk, reloading) {
  if (reloading) {
    return {
      title: "Volvamed se refait une beauté",
      text: "Une nouvelle version vient d'arriver. On recharge la page pour vous, un instant…",
    };
  }
  if (staleChunk) {
    return {
      title: "Volvamed s'est refait une beauté",
      text: "Une nouvelle version est en ligne. Rechargez la page pour en profiter.",
    };
  }
  if (isRouteErrorResponse(error) && error.status === 404) {
    return {
      title: "Cette page a pris le large",
      text: "Elle n'existe plus ou a changé d'adresse. Rien de grave : l'accueil vous attend.",
    };
  }
  return {
    title: "Petite pause, on respire",
    text: "Cette page a besoin d'un instant pour se remettre en place. Rechargez-la, ou revenez à l'accueil.",
  };
}

export default function RouteError() {
  const error = useRouteError();
  const staleChunk = isStaleChunkError(error);
  const [reloading, setReloading] = useState(staleChunk);
  const content = getContent(error, staleChunk, reloading);

  function handleReload() {
    window.location.reload();
  }

  useEffect(() => {
    recordError(error, {
      expected: staleChunk,
      source: "router",
      operation: "routeError",
    });
    if (!staleChunk) return;
    if (pageUnloading || !claimStaleChunkReload()) {
      setReloading(false);
      return;
    }
    window.location.reload();
  }, [error, staleChunk]);

  return (
    <main className="bg-background-secondary flex min-h-svh items-center justify-center px-4 py-12">
      <div className="flex w-full max-w-md flex-col items-center gap-6 rounded-3xl bg-white px-6 py-10 text-center shadow-[0_8px_24px_rgba(38,85,141,0.08)] sm:px-10">
        <CalmIllustration className="w-48 sm:w-56" />
        <div className="flex flex-col gap-3">
          <h1 className="font-heading text-text-primary text-2xl leading-tight font-bold text-balance sm:text-3xl">
            {content.title}
          </h1>
          <p className="text-text-secondary text-base text-pretty">
            {content.text}
          </p>
        </div>
        {!reloading && (
          <div className="flex w-full flex-col-reverse gap-3 sm:w-auto sm:flex-row">
            <Link
              to="/"
              className="border-stroke-primary text-text-primary hover:border-primary hover:text-primary inline-flex min-h-11 items-center justify-center rounded-full border px-5 text-sm font-semibold transition-[color,border-color,scale] duration-150 active:scale-[0.96] motion-reduce:active:scale-[0.995] md:min-h-10"
            >
              Retour à l{"'"}accueil
            </Link>
            <button
              type="button"
              onClick={handleReload}
              className="bg-primary inline-flex min-h-11 items-center justify-center rounded-full px-5 text-sm font-semibold text-white transition-[filter,scale] duration-150 select-none hover:brightness-110 active:scale-[0.96] motion-reduce:active:scale-[0.995] md:min-h-10"
            >
              Recharger la page
            </button>
          </div>
        )}
      </div>
    </main>
  );
}
